import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/environment';

const AddTimeTable = () => {
  const navigate = useNavigate();
  const { currentUser, token } = useAuth(); // Get both currentUser and token from AuthContext
  
  // Section 1 state
  const [section1Data, setSection1Data] = useState({
    degree: [], // Changed to array for multiple selection
    year: '',
    semester: '',
    academicYear: '',
    groups: [] // Changed to array for multiple selection
  });

  // Section 2 state - array to handle multiple sections
  const [section2Data, setSection2Data] = useState([{
    id: 1,
    selectModules: '',
    startTime: '',
    endTime: '',
    dayOfWeek: '',
    venue: '',
    classType: '',
    lecturer: '',
    assistentLecturer: ''
  }]);

  // State for dropdown options
  const [degrees, setDegrees] = useState([]);
  const [degreesLoaded, setDegreesLoaded] = useState(false); // Add debugging state
  const [modules, setModules] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]); // New state for filtered modules based on Section 1 selections
  const [lecturers, setLecturers] = useState([]);
  const [filteredLecturers, setFilteredLecturers] = useState({}); // Object to store lecturers for each section based on selected module
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUniversityId, setCurrentUniversityId] = useState(null);
  const [authError, setAuthError] = useState(null); // Add auth error state

  // Fetch current admin's university and related data
  useEffect(() => {
    if (currentUser) {
      fetchUniversityData();
    }
  }, [currentUser]);

  // Fetch groups when degree, year, semester, or academic year changes
  useEffect(() => {
    if (section1Data.degree.length > 0 && section1Data.year && section1Data.semester && section1Data.academicYear) {
      fetchGroups();
      fetchFilteredModules(); // Also fetch modules when Section 1 data changes
    } else {
      setGroups({});
      setFilteredModules([]); // Reset filtered modules
      setFilteredLecturers({}); // Reset filtered lecturers
      setSection1Data(prev => ({ ...prev, groups: [] }));
    }
  }, [section1Data.degree, section1Data.year, section1Data.semester, section1Data.academicYear]);

  const fetchGroups = async () => {
    try {
      if (!currentUniversityId || section1Data.degree.length === 0) return;

      // Extract year number from "Year X" format
      const yearNumber = parseInt(section1Data.year.replace('Year ', ''));
      // Extract semester number from "Semester X" format  
      const semesterNumber = parseInt(section1Data.semester.replace('Semester ', ''));

      // Fetch groups with degree information
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select(`
          id, 
          group_name, 
          degree_details_id,
          degree_details:degree_details_id (
            id,
            degree_programme,
            degree_level
          )
        `)
        .eq('university_id', currentUniversityId)
        .eq('year', yearNumber)
        .eq('semester', semesterNumber)
        .eq('academic_year', section1Data.academicYear)
        .in('degree_details_id', section1Data.degree)
        .eq('active', true)
        .order('degree_details_id')
        .order('group_name');

      if (groupsError) {
        console.error('Error fetching groups:', groupsError);
      } else {
        // Organize groups by degree
        const groupsByDegree = {};
        (groupsData || []).forEach(group => {
          const degreeId = group.degree_details_id;
          if (!groupsByDegree[degreeId]) {
            groupsByDegree[degreeId] = {
              degree: group.degree_details,
              groups: []
            };
          }
          groupsByDegree[degreeId].groups.push(group);
        });
        setGroups(groupsByDegree);
      }
    } catch (error) {
      console.error('Error in fetchGroups:', error);
    }
  };

  const fetchFilteredModules = async () => {
    try {
      if (!currentUniversityId || section1Data.degree.length === 0 || !section1Data.year || !section1Data.semester) return;

      // Extract year number from "Year X" format
      const yearNumber = parseInt(section1Data.year.replace('Year ', ''));
      // Extract semester number from "Semester X" format  
      const semesterNumber = parseInt(section1Data.semester.replace('Semester ', ''));

      // Fetch modules that are mapped to the selected degrees, year, and semester
      const { data: moduleData, error: moduleError } = await supabase
        .from('module_degree_mappings')
        .select(`
          module_id,
          degree_details_id,
          modules:module_id (
            id,
            module_name,
            module_code,
            semester,
            university_id,
            active
          )
        `)
        .in('degree_details_id', section1Data.degree)
        .eq('year', yearNumber)
        .eq('modules.university_id', currentUniversityId)
        .eq('modules.semester', semesterNumber)
        .eq('modules.active', true);

      if (moduleError) {
        console.error('Error fetching filtered modules:', moduleError);
      } else {
        if (section1Data.degree.length === 1) {
          // If only one degree is selected, show all modules for that degree
          const uniqueModules = [];
          const moduleIds = new Set();
          
          (moduleData || []).forEach(mapping => {
            if (mapping.modules && !moduleIds.has(mapping.modules.id)) {
              moduleIds.add(mapping.modules.id);
              uniqueModules.push(mapping.modules);
            }
          });
          
          // Sort modules by module code
          uniqueModules.sort((a, b) => a.module_code.localeCompare(b.module_code));
          setFilteredModules(uniqueModules);
        } else {
          // If multiple degrees are selected, show only common modules
          // Count how many degrees each module appears in
          const moduleCountByDegree = {};
          
          (moduleData || []).forEach(mapping => {
            if (mapping.modules) {
              const moduleId = mapping.modules.id;
              if (!moduleCountByDegree[moduleId]) {
                moduleCountByDegree[moduleId] = {
                  module: mapping.modules,
                  degreeCount: new Set()
                };
              }
              moduleCountByDegree[moduleId].degreeCount.add(mapping.degree_details_id);
            }
          });
          
          // Filter modules that appear in ALL selected degrees
          const commonModules = [];
          const selectedDegreeCount = section1Data.degree.length;
          
          Object.values(moduleCountByDegree).forEach(({ module, degreeCount }) => {
            if (degreeCount.size === selectedDegreeCount) {
              commonModules.push(module);
            }
          });
          
          // Sort modules by module code
          commonModules.sort((a, b) => a.module_code.localeCompare(b.module_code));
          setFilteredModules(commonModules);
        }
      }
    } catch (error) {
      console.error('Error in fetchFilteredModules:', error);
    }
  };

  const fetchLecturersForModule = async (moduleId, sectionId) => {
    try {
      if (!currentUniversityId || !moduleId) return;

      // Fetch lecturers who can teach this specific module
      const { data: lecturerData, error: lecturerError } = await supabase
        .from('staff_courses')
        .select(`
          staff_id,
          can_teach_as_primary,
          can_teach_as_assistant,
          staff:staff_id (
            id,
            name,
            staff_type,
            university_id,
            active
          )
        `)
        .eq('module_id', moduleId)
        .eq('staff.university_id', currentUniversityId)
        .eq('staff.active', true)
        .in('staff.staff_type', ['lecturer', 'professor', 'assistant_lecturer']);

      if (lecturerError) {
        console.error('Error fetching lecturers for module:', lecturerError);
      } else {
        // Organize lecturers by capability (primary vs assistant)
        const availableLecturers = {
          primary: [],
          assistant: []
        };

        (lecturerData || []).forEach(course => {
          if (course.staff) {
            if (course.can_teach_as_primary) {
              availableLecturers.primary.push(course.staff);
            }
            if (course.can_teach_as_assistant) {
              availableLecturers.assistant.push(course.staff);
            }
          }
        });

        // Remove duplicates and sort by name
        availableLecturers.primary = [...new Map(availableLecturers.primary.map(l => [l.id, l])).values()]
          .sort((a, b) => a.name.localeCompare(b.name));
        availableLecturers.assistant = [...new Map(availableLecturers.assistant.map(l => [l.id, l])).values()]
          .sort((a, b) => a.name.localeCompare(b.name));

        // Update filtered lecturers for this specific section
        setFilteredLecturers(prev => ({
          ...prev,
          [sectionId]: availableLecturers
        }));
      }
    } catch (error) {
      console.error('Error in fetchLecturersForModule:', error);
    }
  };

  const fetchUniversityData = async () => {
    try {
      setLoading(true);
      setAuthError(null); // Reset auth error
      
      console.log('🔍 Starting fetchUniversityData...');
      
      // Check if user is authenticated through AuthContext
      if (!currentUser || !currentUser.email) {
        console.error('❌ No authenticated user found in AuthContext');
        setAuthError('Authentication failed. Please log in again.');
        setLoading(false);
        return;
      }

      console.log('✅ Current authenticated user from AuthContext:', currentUser.email);
      console.log('👤 Current user role:', currentUser.role);
      console.log('🆔 Current user ID:', currentUser.id);

      // Verify this is an admin user
      if (currentUser.role !== 'admin') {
        console.error('❌ User is not an admin:', currentUser.role);
        setAuthError('Access denied. This page is for administrators only.');
        setLoading(false);
        return;
      }

      // Get admin's university_id with more detailed logging
      console.log('🔍 Searching for admin with email:', currentUser.email);
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('university_id, id, email')
        .eq('email', currentUser.email)
        .single();

      console.log('📋 Admin query result:', { adminData, adminError });

      if (adminError || !adminData) {
        console.error('❌ Error fetching admin data:', adminError);
        console.error('❌ Admin not found for email:', currentUser.email);
        
        // Let's also check what admins exist in the database
        console.log('🔍 Checking all admins in database...');
        const { data: allAdmins, error: allAdminsError } = await supabase
          .from('admins')
          .select('email, university_id, active');
        
        console.log('📋 All admins in database:', allAdmins);
        console.log('📋 All admins query error:', allAdminsError);
        
        setAuthError('Admin access not found. Please contact your system administrator.');
        setLoading(false);
        return;
      }
      
      const universityId = adminData.university_id;
      setCurrentUniversityId(universityId);
      
      console.log('✅ Admin found!');
      console.log('🎓 Admin university ID:', universityId);
      console.log('👤 Admin ID:', adminData.id);
      console.log('📧 Admin email:', adminData.email);

      // Fetch degrees for this university only
      console.log('🔍 Fetching degrees for university ID:', universityId);
      const { data: degreesData, error: degreesError } = await supabase
        .from('degree_details')
        .select('id, degree_programme, degree_level, local_international, university_id')
        .eq('university_id', universityId)
        .eq('active', true)
        .order('degree_programme');

      console.log('📋 Degrees query result:', { degreesData, degreesError });

      if (degreesError) {
        console.error('❌ Error fetching degrees:', degreesError);
        setDegrees([]);
        setDegreesLoaded(true);
      } else {
        console.log('✅ Fetched degrees for university:', degreesData);
        console.log('📊 Number of degrees found:', degreesData?.length || 0);
        
        // Log each degree with its university ID for verification
        (degreesData || []).forEach((degree, index) => {
          console.log(`📝 Degree ${index + 1}: "${degree.degree_programme}" (University ID: ${degree.university_id})`);
        });
        
        setDegrees(degreesData || []);
        setDegreesLoaded(true);
      }

      // Let's also check if there are degrees from other universities (for debugging)
      console.log('🔍 Checking degrees from other universities (for debugging)...');
      const { data: allDegreesData, error: allDegreesError } = await supabase
        .from('degree_details')
        .select('id, degree_programme, university_id, universities:university_id(name)')
        .eq('active', true)
        .order('degree_programme');
      
      console.log('📋 All degrees in database:', allDegreesData);
      if (allDegreesData) {
        const otherUniversityDegrees = allDegreesData.filter(d => d.university_id !== universityId);
        console.log('🚨 Degrees from other universities:', otherUniversityDegrees);
      }

      // Fetch modules for this university only
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select('id, module_name, module_code')
        .eq('university_id', universityId)
        .eq('active', true)
        .order('module_name');

      if (modulesError) {
        console.error('Error fetching modules:', modulesError);
      } else {
        console.log('Fetched modules for university:', modulesData);
        setModules(modulesData || []);
      }

      // Fetch lecturers for this university only
      const { data: lecturersData, error: lecturersError } = await supabase
        .from('staff')
        .select('id, name, staff_type')
        .eq('university_id', universityId)
        .eq('active', true)
        .in('staff_type', ['lecturer', 'professor'])
        .order('name');

      if (lecturersError) {
        console.error('Error fetching lecturers:', lecturersError);
      } else {
        console.log('Fetched lecturers for university:', lecturersData);
        setLecturers(lecturersData || []);
      }

    } catch (error) {
      console.error('Error in fetchUniversityData:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSection1Change = (e) => {
    const { name, value } = e.target;
    
    setSection1Data(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'year' || name === 'semester' || name === 'academicYear' ? { groups: [] } : {})
    }));
  };

  const handleSection2Change = (id, field, value) => {
    setSection2Data(prev => 
      prev.map(item => 
        item.id === id ? { 
          ...item, 
          [field]: value,
          // Reset lecturer fields when module changes
          ...(field === 'selectModules' ? { lecturer: '', assistentLecturer: '' } : {})
        } : item
      )
    );

    // Fetch lecturers when module is selected
    if (field === 'selectModules' && value) {
      fetchLecturersForModule(value, id);
    }
  };

  const addNewSection2 = () => {
    const newId = Math.max(...section2Data.map(item => item.id)) + 1;
    setSection2Data(prev => [...prev, {
      id: newId,
      selectModules: '',
      startTime: '',
      endTime: '',
      dayOfWeek: '',
      venue: '',
      classType: '',
      lecturer: '',
      assistentLecturer: ''
    }]);
  };

  const removeSection2 = (id) => {
    if (section2Data.length > 1) {
      setSection2Data(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      // Validate section 1 data
      if (section1Data.degree.length === 0 || !section1Data.year || !section1Data.semester || 
          !section1Data.academicYear || section1Data.groups.length === 0) {
        alert('Please complete Section 1 with all required fields.');
        setLoading(false);
        return;
      }

      // Get current admin ID from AuthContext
      if (!currentUser || !currentUser.id || currentUser.role !== 'admin') {
        alert('Admin access required. Please log in as an administrator.');
        setLoading(false);
        return;
      }

      if (!token) {
        alert('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Extract year and semester numbers
      const yearNumber = parseInt(section1Data.year.replace('Year ', ''));
      const semesterNumber = parseInt(section1Data.semester.replace('Semester ', ''));

      // Prepare timetable entries for each section
      const timetableEntries = [];
      
      for (const section of section2Data) {
        // Validate required fields
        if (!section.selectModules || !section.startTime || !section.endTime || 
            !section.dayOfWeek || !section.venue || !section.classType || !section.lecturer) {
          alert('Please fill in all required fields for all class sections.');
          setLoading(false);
          return;
        }

        // Always store time as UTC, matching the selected value (no local timezone shift)
        // Use a fixed date (1970-01-01) for time-only semantics
        const [startHour, startMinute] = section.startTime.split(':');
        const [endHour, endMinute] = section.endTime.split(':');

        // Construct UTC date string for 1970-01-01 at selected time
        const startDateTime = new Date(Date.UTC(1970, 0, 1, parseInt(startHour), parseInt(startMinute), 0, 0));
        const endDateTime = new Date(Date.UTC(1970, 0, 1, parseInt(endHour), parseInt(endMinute), 0, 0));

        const timetableEntry = {
          university_id: currentUniversityId,
          module_id: section.selectModules,
          lecturer_id: section.lecturer,
          assistant_lecturer_id: section.assistentLecturer || null,
          group_ids: section1Data.groups, // Array of group IDs
          degree_details_ids: section1Data.degree, // Array of degree IDs
          year: yearNumber,
          semester: semesterNumber,
          academic_year: section1Data.academicYear,
          day_of_week: section.dayOfWeek,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          venue: section.venue,
          class_type: section.classType,
          created_by: currentUser.id,
          active: true
        };

        timetableEntries.push(timetableEntry);
      }

      console.log('Submitting timetable entries:', timetableEntries);

      // Make API call to create timetable entries
      const response = await fetch(config.getApiUrl('/timetables/bulk'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // Use token from AuthContext
        },
        body: JSON.stringify({
          timetable_entries: timetableEntries
        })
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('API Error:', result);
        alert(result.error || 'Error creating timetable entries. Please try again.');
        setLoading(false);
        return;
      }

      console.log('Successfully created timetable entries:', result);
      alert(`Successfully created ${timetableEntries.length} timetable entries!`);
      
      // Navigate back to timetable management
      navigate('/timetable-management');

    } catch (error) {
      console.error('Error in handleSubmit:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Custom styles for dropdown options */}
      <style>
        {`
          select option:hover {
            background-color: #FEF3F2 !important;
            color: #F04915 !important;
          }
          select option:checked {
            background-color: #F04915 !important;
            color: white !important;
          }
          select:focus option:hover {
            background-color: #FEF3F2 !important;
            color: #F04915 !important;
          }
          select option {
            padding: 8px 12px !important;
            font-family: 'Outfit' !important;
            font-size: 15px !important;
          }
          select::-webkit-scrollbar {
            width: 6px;
          }
          select::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 6px;
          }
          select::-webkit-scrollbar-thumb {
            background: #F04915;
            border-radius: 6px;
          }
          select::-webkit-scrollbar-thumb:hover {
            background: #D63A0F;
          }
        `}
      </style>
      
      <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
        <Sidebar />
        
        <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
          <Header />
          
          {/* Page Title */}
          <h1 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', marginTop: '20px', paddingLeft: '40px'}}>
            Add Time Table Entry
          </h1>

        {/* Form Container */}
        <div style={{marginTop: '20px', margin: '20px 40px'}}>
          <div style={{background: '#F04915', padding: '20px', borderTopLeftRadius: 15, borderTopRightRadius: 15}}>
            <h2 style={{color: 'white', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', margin: 0}}>
              Create New Time Table Entry
            </h2>
          </div>

          <div style={{
            background: '#FFFBF5', 
            padding: '40px',
            borderBottomLeftRadius: 15,
            borderBottomRightRadius: 15,
            minHeight: '400px'
          }}>
            <form onSubmit={handleSubmit}>
              {/* Section 1 */}
              <div style={{
                background: 'white',
                padding: '35px',
                borderRadius: '15px',
                marginBottom: '25px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid #F0F0F0'
              }}>
                <h3 style={{
                  fontSize: '20px',
                  fontFamily: 'Outfit',
                  fontWeight: '600',
                  color: '#F04915',
                  marginBottom: '30px',
                  borderBottom: '2px solid #F04915',
                  paddingBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{
                    backgroundColor: '#F04915',
                    color: 'white',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700'
                  }}>
                    1
                  </span>
                  Section 1: Basic Information
                </h3>
                
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px'}}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '12px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333',
                      fontFamily: 'Outfit'
                    }}>Degree * (Select multiple degrees)</label>
                    
                    <div style={{
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      padding: '16px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: loading ? '#f5f5f5' : '#fff'
                    }}>
                      {loading ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: '#666',
                          fontFamily: 'Outfit'
                        }}>
                          Loading degrees...
                        </div>
                      ) : authError ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: '#dc3545',
                          fontFamily: 'Outfit'
                        }}>
                          {authError}
                        </div>
                      ) : degrees.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: '#666',
                          fontFamily: 'Outfit'
                        }}>
                          No degree programmes found for your university. Please contact your system administrator.
                        </div>
                      ) : (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                          gap: '8px'
                        }}>
                          {degrees.map((degree) => (
                            <label key={degree.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 12px',
                              border: '1px solid #E5E7EB',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              backgroundColor: section1Data.degree.includes(degree.id) ? '#FEF3F2' : 'white',
                              borderColor: section1Data.degree.includes(degree.id) ? '#F04915' : '#E5E7EB',
                              fontFamily: 'Outfit',
                              fontSize: '13px'
                            }}
                            onMouseOver={(e) => {
                              if (!section1Data.degree.includes(degree.id)) {
                                e.currentTarget.style.backgroundColor = '#F9FAFB';
                                e.currentTarget.style.borderColor = '#D1D5DB';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!section1Data.degree.includes(degree.id)) {
                                e.currentTarget.style.backgroundColor = 'white';
                                e.currentTarget.style.borderColor = '#E5E7EB';
                              }
                            }}
                            >
                              <input
                                type="checkbox"
                                checked={section1Data.degree.includes(degree.id)}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setSection1Data(prev => ({
                                    ...prev,
                                    degree: isChecked 
                                      ? [...prev.degree, degree.id]
                                      : prev.degree.filter(id => id !== degree.id),
                                    groups: [] // Reset groups when degrees change
                                  }));
                                }}
                                style={{
                                  marginRight: '8px',
                                  accentColor: '#F04915',
                                  transform: 'scale(1.1)'
                                }}
                              />
                              <span style={{
                                color: section1Data.degree.includes(degree.id) ? '#F04915' : '#374151',
                                fontWeight: section1Data.degree.includes(degree.id) ? '500' : '400'
                              }}>
                                {degree.degree_programme} ({degree.degree_level})
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Selection Summary */}
                    {section1Data.degree.length > 0 && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#F0FDF4',
                        border: '1px solid #BBF7D0',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontFamily: 'Outfit',
                        color: '#15803D'
                      }}>
                        <strong>{section1Data.degree.length}</strong> degree(s) selected
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333',
                      fontFamily: 'Outfit'
                    }}>Year *</label>
                    <select
                      name="year"
                      value={section1Data.year}
                      onChange={handleSection1Change}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #E0E0E0',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontFamily: 'Outfit',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        minHeight: '48px',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F04915' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '20px',
                        paddingRight: '40px'
                      }}
                      required
                      onFocus={(e) => {
                        e.target.style.borderColor = '#F04915';
                        e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E0E0E0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="" style={{ color: '#999', backgroundColor: '#fff', padding: '8px 12px' }}>Select Year</option>
                      <option value="Year 1" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Year 1</option>
                      <option value="Year 2" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Year 2</option>
                      <option value="Year 3" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Year 3</option>
                      <option value="Year 4" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Year 4</option>
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333',
                      fontFamily: 'Outfit'
                    }}>Semester *</label>
                    <select
                      name="semester"
                      value={section1Data.semester}
                      onChange={handleSection1Change}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #E0E0E0',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontFamily: 'Outfit',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        minHeight: '48px',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F04915' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '20px',
                        paddingRight: '40px'
                      }}
                      required
                      onFocus={(e) => {
                        e.target.style.borderColor = '#F04915';
                        e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E0E0E0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="" style={{ color: '#999', backgroundColor: '#fff', padding: '8px 12px' }}>Select Semester</option>
                      <option value="Semester 1" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Semester 1</option>
                      <option value="Semester 2" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Semester 2</option>
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333',
                      fontFamily: 'Outfit'
                    }}>Academic Year *</label>
                    <select
                      name="academicYear"
                      value={section1Data.academicYear}
                      onChange={handleSection1Change}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #E0E0E0',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontFamily: 'Outfit',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        minHeight: '48px',
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F04915' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                        backgroundSize: '20px',
                        paddingRight: '40px'
                      }}
                      required
                      onFocus={(e) => {
                        e.target.style.borderColor = '#F04915';
                        e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#E0E0E0';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="" style={{ color: '#999', backgroundColor: '#fff', padding: '8px 12px' }}>Select Academic Year</option>
                      <option value="2023-2024" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>2023-2024</option>
                      <option value="2024-2025" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>2024-2025</option>
                      <option value="2025-2026" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>2025-2026</option>
                      <option value="2026-2027" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>2026-2027</option>
                      <option value="2027-2028" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>2027-2028</option>
                      <option value="2028-2029" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>2028-2029</option>
                      <option value="2029-2030" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>2029-2030</option>
                    </select>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '12px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333',
                      fontFamily: 'Outfit'
                    }}>Groups * (Select multiple groups across degrees)</label>
                    
                    <div style={{
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      padding: '16px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      backgroundColor: (section1Data.degree.length === 0 || !section1Data.year || !section1Data.semester || !section1Data.academicYear) ? '#f5f5f5' : '#fff'
                    }}>
                      {loading ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: '#666',
                          fontFamily: 'Outfit'
                        }}>
                          Loading groups...
                        </div>
                      ) : (section1Data.degree.length === 0 || !section1Data.year || !section1Data.semester || !section1Data.academicYear) ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: '#666',
                          fontFamily: 'Outfit'
                        }}>
                          Please select degree(s), year, semester, and academic year first
                        </div>
                      ) : Object.keys(groups).length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: '#666',
                          fontFamily: 'Outfit'
                        }}>
                          No groups available for selected criteria
                        </div>
                      ) : (
                        Object.entries(groups).map(([degreeId, degreeData]) => (
                          <div key={degreeId} style={{ marginBottom: '20px' }}>
                            {/* Degree Header */}
                            <div style={{
                              background: 'linear-gradient(135deg, #F04915 0%, #FF6B35 100%)',
                              color: 'white',
                              padding: '12px 16px',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600',
                              fontFamily: 'Outfit',
                              marginBottom: '12px',
                              boxShadow: '0 2px 4px rgba(240, 73, 21, 0.2)'
                            }}>
                              {degreeData.degree.degree_programme} ({degreeData.degree.degree_level})
                            </div>
                            
                            {/* Groups Grid */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                              gap: '8px',
                              paddingLeft: '16px'
                            }}>
                              {degreeData.groups.map((group) => (
                                <label key={group.id} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  backgroundColor: section1Data.groups.includes(group.id) ? '#FEF3F2' : 'white',
                                  borderColor: section1Data.groups.includes(group.id) ? '#F04915' : '#E5E7EB',
                                  fontFamily: 'Outfit',
                                  fontSize: '13px'
                                }}
                                onMouseOver={(e) => {
                                  if (!section1Data.groups.includes(group.id)) {
                                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                                    e.currentTarget.style.borderColor = '#D1D5DB';
                                  }
                                }}
                                onMouseOut={(e) => {
                                  if (!section1Data.groups.includes(group.id)) {
                                    e.currentTarget.style.backgroundColor = 'white';
                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                  }
                                }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={section1Data.groups.includes(group.id)}
                                    onChange={(e) => {
                                      const isChecked = e.target.checked;
                                      setSection1Data(prev => ({
                                        ...prev,
                                        groups: isChecked 
                                          ? [...prev.groups, group.id]
                                          : prev.groups.filter(id => id !== group.id)
                                      }));
                                    }}
                                    style={{
                                      marginRight: '8px',
                                      accentColor: '#F04915',
                                      transform: 'scale(1.1)'
                                    }}
                                  />
                                  <span style={{
                                    color: section1Data.groups.includes(group.id) ? '#F04915' : '#374151',
                                    fontWeight: section1Data.groups.includes(group.id) ? '500' : '400'
                                  }}>
                                    {group.group_name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Selection Summary */}
                    {section1Data.groups.length > 0 && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#F0FDF4',
                        border: '1px solid #BBF7D0',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontFamily: 'Outfit',
                        color: '#15803D'
                      }}>
                        <strong>{section1Data.groups.length}</strong> group(s) selected for this time table
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2 - Multiple instances */}
              {section2Data.map((section, index) => (
                <div key={section.id} style={{
                  background: 'white',
                  padding: '35px',
                  borderRadius: '15px',
                  marginBottom: '25px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  position: 'relative',
                  border: '1px solid #F0F0F0'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '30px'
                  }}>
                    <h3 style={{
                      fontSize: '20px',
                      fontFamily: 'Outfit',
                      fontWeight: '600',
                      color: '#F04915',
                      borderBottom: '2px solid #F04915',
                      paddingBottom: '10px',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <span style={{
                        backgroundColor: '#F04915',
                        color: 'white',
                        borderRadius: '50%',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700'
                      }}>
                        {index + 1}
                      </span>
                      Section 2: Class Details {index + 1}
                    </h3>
                    {section2Data.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSection2(section.id)}
                        style={{
                          background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          fontSize: '13px',
                          fontFamily: 'Outfit',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                        }}
                      >
                        🗑️ Remove
                      </button>
                    )}
                  </div>
                  
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px'}}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        fontFamily: 'Outfit'
                      }}>Select Modules *</label>
                      <select
                        value={section.selectModules}
                        onChange={(e) => handleSection2Change(section.id, 'selectModules', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontFamily: 'Outfit',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          backgroundColor: (section1Data.degree.length === 0 || !section1Data.year || !section1Data.semester) ? '#f5f5f5' : '#fff',
                          cursor: 'pointer',
                          minHeight: '48px',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F04915' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '20px',
                          paddingRight: '40px'
                        }}
                        required
                        disabled={loading || section1Data.degree.length === 0 || !section1Data.year || !section1Data.semester}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#F04915';
                          e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#E0E0E0';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <option value="" style={{ color: '#999', backgroundColor: '#fff', padding: '8px 12px' }}>
                          {loading ? 'Loading modules...' : 
                           (section1Data.degree.length === 0 || !section1Data.year || !section1Data.semester) ? 
                           'Please complete Section 1 first' : 
                           filteredModules.length === 0 ? 'No modules available for selected criteria' :
                           'Select Module'}
                        </option>
                        {filteredModules.map((module) => (
                          <option key={module.id} value={module.id} style={{ 
                            backgroundColor: '#fff',
                            color: '#333',
                            padding: '8px 12px'
                          }}>
                            {module.module_code} - {module.module_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        fontFamily: 'Outfit'
                      }}>Start Time *</label>
                      <input
                        type="time"
                        value={section.startTime}
                        onChange={(e) => handleSection2Change(section.id, 'startTime', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontFamily: 'Outfit',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          minHeight: '48px',
                          cursor: 'pointer'
                        }}
                        required
                        onFocus={(e) => {
                          e.target.style.borderColor = '#F04915';
                          e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#E0E0E0';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        fontFamily: 'Outfit'
                      }}>End Time *</label>
                      <input
                        type="time"
                        value={section.endTime}
                        onChange={(e) => handleSection2Change(section.id, 'endTime', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontFamily: 'Outfit',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          minHeight: '48px',
                          cursor: 'pointer'
                        }}
                        required
                        onFocus={(e) => {
                          e.target.style.borderColor = '#F04915';
                          e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#E0E0E0';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        fontFamily: 'Outfit'
                      }}>Day of Week *</label>
                      <select
                        value={section.dayOfWeek}
                        onChange={(e) => handleSection2Change(section.id, 'dayOfWeek', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontFamily: 'Outfit',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          minHeight: '48px',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F04915' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '20px',
                          paddingRight: '40px'
                        }}
                        required
                        onFocus={(e) => {
                          e.target.style.borderColor = '#F04915';
                          e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#E0E0E0';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <option value="" style={{ color: '#999', backgroundColor: '#fff', padding: '8px 12px' }}>Select Day</option>
                        <option value="Monday" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Monday</option>
                        <option value="Tuesday" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Tuesday</option>
                        <option value="Wednesday" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Wednesday</option>
                        <option value="Thursday" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Thursday</option>
                        <option value="Friday" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Friday</option>
                        <option value="Saturday" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Saturday</option>
                        <option value="Sunday" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Sunday</option>
                      </select>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        fontFamily: 'Outfit'
                      }}>Venue *</label>
                      <input
                        type="text"
                        value={section.venue}
                        onChange={(e) => handleSection2Change(section.id, 'venue', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontFamily: 'Outfit',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          minHeight: '48px'
                        }}
                        placeholder="e.g., Room 101, Lab A"
                        required
                        onFocus={(e) => {
                          e.target.style.borderColor = '#F04915';
                          e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#E0E0E0';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        fontFamily: 'Outfit'
                      }}>Class Type *</label>
                      <select
                        value={section.classType}
                        onChange={(e) => handleSection2Change(section.id, 'classType', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontFamily: 'Outfit',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          minHeight: '48px',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F04915' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '20px',
                          paddingRight: '40px'
                        }}
                        required
                        onFocus={(e) => {
                          e.target.style.borderColor = '#F04915';
                          e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#E0E0E0';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <option value="" style={{ color: '#999', backgroundColor: '#fff', padding: '8px 12px' }}>Select Class Type</option>
                        <option value="Lecture" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Lecture</option>
                        <option value="Tutorial" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Tutorial</option>
                        <option value="Practical" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Practical</option>
                        <option value="Laboratory" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Laboratory</option>
                        <option value="Workshop" style={{ backgroundColor: '#fff', color: '#333', padding: '8px 12px' }}>Workshop</option>
                      </select>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        fontFamily: 'Outfit'
                      }}>Lecturer *</label>
                      <select
                        value={section.lecturer}
                        onChange={(e) => handleSection2Change(section.id, 'lecturer', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontFamily: 'Outfit',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          backgroundColor: !section.selectModules ? '#f5f5f5' : '#fff',
                          cursor: 'pointer',
                          minHeight: '48px',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F04915' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '20px',
                          paddingRight: '40px'
                        }}
                        required
                        disabled={loading || !section.selectModules}
                        onFocus={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.borderColor = '#F04915';
                            e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#E0E0E0';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <option value="" style={{ color: '#999', backgroundColor: '#fff' }}>
                          {loading ? 'Loading lecturers...' : 
                           !section.selectModules ? 'Please select a module first' :
                           !filteredLecturers[section.id]?.primary?.length ? 'No lecturers available for this module' :
                           'Select Lecturer'}
                        </option>
                        {(filteredLecturers[section.id]?.primary || []).map((lecturer) => (
                          <option key={lecturer.id} value={lecturer.id} style={{ 
                            backgroundColor: '#fff',
                            color: '#333',
                            padding: '8px'
                          }}>
                            {lecturer.name} ({lecturer.staff_type})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#333',
                        fontFamily: 'Outfit'
                      }}>Assistant Lecturer</label>
                      <select
                        value={section.assistentLecturer}
                        onChange={(e) => handleSection2Change(section.id, 'assistentLecturer', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontFamily: 'Outfit',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          backgroundColor: !section.selectModules ? '#f5f5f5' : '#fff',
                          cursor: 'pointer',
                          minHeight: '48px',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23F04915' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '20px',
                          paddingRight: '40px'
                        }}
                        disabled={loading || !section.selectModules}
                        onFocus={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.borderColor = '#F04915';
                            e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#E0E0E0';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        <option value="" style={{ color: '#999', backgroundColor: '#fff' }}>
                          {loading ? 'Loading lecturers...' : 
                           !section.selectModules ? 'Please select a module first' :
                           'Select Assistant Lecturer (Optional)'}
                        </option>
                        {(filteredLecturers[section.id]?.assistant || []).map((lecturer) => (
                          <option key={lecturer.id} value={lecturer.id} style={{ 
                            backgroundColor: '#fff',
                            color: '#333',
                            padding: '8px'
                          }}>
                            {lecturer.name} ({lecturer.staff_type})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add One Button */}
              <div style={{textAlign: 'center', marginBottom: '35px'}}>
                <button
                  type="button"
                  onClick={addNewSection2}
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '30px',
                    padding: '15px 35px',
                    fontSize: '16px',
                    fontFamily: 'Outfit',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 6px 20px rgba(16, 185, 129, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    margin: '0 auto'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <span style={{ fontSize: '18px' }}>➕</span>
                  Add Another Class
                </button>
              </div>

              {/* Form Actions */}
              <div style={{display: 'flex', gap: '15px', justifyContent: 'center'}}>
                <button 
                  type="submit" 
                  style={{
                    padding: '15px 30px', 
                    background: '#F04915', 
                    border: 'none', 
                    borderRadius: 8, 
                    color: 'white', 
                    fontSize: 16, 
                    fontFamily: 'Outfit', 
                    fontWeight: '500', 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#D63A0F';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#F04915';
                  }}
                >
                  Create Time Table
                </button>

                <Link to="/timetable-management">
                  <button 
                    type="button" 
                    style={{
                      padding: '15px 30px', 
                      background: '#6B7280', 
                      border: 'none', 
                      borderRadius: 8, 
                      color: 'white', 
                      fontSize: 16, 
                      fontFamily: 'Outfit', 
                      fontWeight: '500', 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#4B5563';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = '#6B7280';
                    }}
                  >
                    Cancel
                  </button>
                </Link>
              </div>
            </form>
          </div>
        </div>

        {/* Back Button */}
        <div style={{display: 'flex', justifyContent: 'flex-start', margin: '40px', marginTop: '20px'}}>
          <Link to="/timetable-management">
            <button 
              type="button" 
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#D63A0F';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#F04915';
              }}
            >
              Back to Time Table Management
            </button>
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default AddTimeTable;
